import { useEffect, useState, type FormEvent } from 'react';
import { Cross } from './AppIcons';
import {
  getEmployee,
  updateEmployee,
  type ContractType,
  type EmployeeDto,
} from '../../lib/entities/employees';
import { listEntityQuestions, type EntityQuestionValue } from '../../lib/entities/entityQuestions';

const CONTRACT_OPTIONS: { value: ContractType; label: string }[] = [
  { value: 'fullTime', label: 'Full-time' },
  { value: 'partTime', label: 'Part-time' },
  { value: 'casual', label: 'Casual' },
];

/** Displayed answers keyed by predicate slug; null means never answered. */
type Answers = Record<string, boolean | null>;

function answersOf(questions: EntityQuestionValue[]): Answers {
  return Object.fromEntries(questions.map((q) => [q.key, q.value]));
}

function toggleBtn(active: boolean): string {
  return `btn btn-sm ${active ? 'btn-primary' : 'btn-secondary'}`;
}

/**
 * Edit an employee: the basics, plus the captured per-hire predicate answers that otherwise can only be
 * set once in the add flow. Loads the employee DTO rather than the display type on purpose, because the
 * display type drops `endedOn` and PUT is a full replace, so editing an offboarded person off a display
 * object would quietly bring them back into headcount.
 *
 * Saving a parent answer can unlock child questions (progressive disclosure). Rather than guess at the
 * tree client-side, we save, re-read the applicable list, and stay open when new questions appeared.
 */
export default function EditEmployeeModal({
  employeeId,
  onClose,
  onSaved,
}: {
  employeeId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [dto, setDto] = useState<EmployeeDto | null>(null);
  const [questions, setQuestions] = useState<EntityQuestionValue[]>([]);
  const [answers, setAnswers] = useState<Answers>({});
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [contractType, setContractType] = useState<ContractType>('fullTime');
  const [note, setNote] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Set when a save unlocked further questions, so the extra round is explained rather than surprising.
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([getEmployee(employeeId), listEntityQuestions('/api/employees', employeeId)])
      .then(([employee, list]) => {
        if (!alive) return;
        setDto(employee);
        setName(employee.name);
        setRole(employee.role ?? '');
        setContractType(employee.contractType);
        setNote(employee.note ?? '');
        setQuestions(list.questions);
        setAnswers(answersOf(list.questions));
      })
      .catch((e) => {
        if (alive) setLoadError(e instanceof Error ? e.message : 'Could not load this person.');
      });
    return () => {
      alive = false;
    };
  }, [employeeId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!dto || saving) return;
    setSaving(true);
    setError(null);
    try {
      // Only answered questions are sent. The server replaces the map with exactly this and re-applies
      // the parent gate, so a child orphaned by a parent going false is dropped server-side.
      const predicates: Record<string, boolean> = {};
      for (const q of questions) {
        const value = answers[q.key];
        if (typeof value === 'boolean') predicates[q.key] = value;
      }
      // Only keys that are NEW after the save count as unlocked (a child a just-answered parent revealed).
      // Comparing against "any unanswered question" would match records that predate enforcement and trap
      // the modal open; those are handled by the save-time gate on `unanswered` instead.
      const previousKeys = new Set(questions.map((q) => q.key));

      await updateEmployee(employeeId, {
        name: name.trim(),
        role: role.trim() || undefined,
        contractType,
        note: note.trim() || undefined,
        // Preserve offboarding: PUT replaces the field, so omitting it would un-offboard them.
        endedOn: dto.endedOn ?? undefined,
        predicates,
      });

      const next = await listEntityQuestions('/api/employees', employeeId);
      const newlyUnlocked = next.questions.filter(
        (q) => !previousKeys.has(q.key) && q.value === null,
      );
      if (newlyUnlocked.length > 0) {
        setQuestions(next.questions);
        setAnswers(answersOf(next.questions));
        setUnlocked(true);
        return;
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  // Every applicable predicate must be answered yes/no before saving: leaving one unanswered stores it
  // as absent, which the resolver reads as false and shows the wrong tier. No partial saves.
  const unanswered = questions.filter((q) => typeof answers[q.key] !== 'boolean');

  return (
    <div className="modal-scrim" onClick={onClose} role="presentation">
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="emp-edit-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="modal__close" aria-label="Close" onClick={onClose}>
          <Cross size={18} />
        </button>
        <h2 className="modal__title" id="emp-edit-title">
          Edit employee
        </h2>

        {loadError ? (
          <p className="emp-modal__sub" role="alert">
            {loadError}
          </p>
        ) : !dto ? (
          <p className="emp-modal__sub">Loading…</p>
        ) : (
          <form className="emp-form" onSubmit={handleSave}>
            <label className="field">
              <span className="field__label">Full name</span>
              <input
                className="input"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="field">
              <span className="field__label">Role</span>
              <input className="input" value={role} onChange={(e) => setRole(e.target.value)} />
            </label>
            <div className="emp-form__row">
              <label className="field">
                <span className="field__label">Contract type</span>
                <select
                  className="select"
                  value={contractType}
                  onChange={(e) => setContractType(e.target.value as ContractType)}
                >
                  {CONTRACT_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span className="field__label">Note</span>
                <input className="input" value={note} onChange={(e) => setNote(e.target.value)} />
              </label>
            </div>

            {questions.length > 0 && (
              <fieldset style={{ border: 0, padding: 0, margin: '4px 0 0' }}>
                <legend className="field__label" style={{ padding: 0, marginBottom: 8 }}>
                  About this hire
                </legend>
                <p className="emp-modal__sub" style={{ margin: '0 0 10px' }}>
                  {unlocked
                    ? 'That answer brought up a follow-up. Answer it and save again.'
                    : 'These set which documents apply and at what tier. Each needs a yes or no.'}
                </p>
                {questions.map((q) => (
                  <div
                    key={q.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      padding: '7px 0',
                    }}
                  >
                    <span style={{ fontSize: '13.5px' }}>{q.prompt}</span>
                    <span style={{ display: 'flex', gap: 6, flexShrink: 0 }} role="group" aria-label={q.prompt}>
                      <button
                        type="button"
                        className={toggleBtn(answers[q.key] === true)}
                        onClick={() => setAnswers((prev) => ({ ...prev, [q.key]: true }))}
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        className={toggleBtn(answers[q.key] === false)}
                        onClick={() => setAnswers((prev) => ({ ...prev, [q.key]: false }))}
                      >
                        No
                      </button>
                    </span>
                  </div>
                ))}
              </fieldset>
            )}

            {error && (
              <p style={{ margin: 0, color: 'var(--wine)', fontSize: '13.5px' }} role="alert">
                {error}
              </p>
            )}
            {unanswered.length > 0 && !error && (
              <p className="emp-modal__sub" style={{ margin: 0 }}>
                Answer every question above to save.
              </p>
            )}
            <div className="emp-modal__foot">
              <button type="button" className="btn btn-secondary btn-xs" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-xs"
                disabled={saving || unanswered.length > 0}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
